// Random calamities. Wars are scheduled and telegraphed; calamities are not.
// On a fully random clock (gated past the onboarding window) a disaster strikes
// the settlement: natural, man-made, economic or social. Effects are immediate
// and honest (you watch your stocks drop), never lethal by themselves, and the
// production debuffs ride the existing timed-buff pipeline.

import type { GameState } from '@core/state';
import { BALANCE } from '@data/balance';
import { CALAMITIES, type CalamityDef } from '@data/calamities';
import { RESOURCE_ORDER } from '@data/resources';
import { bus } from '@core/events';
import { tc } from '@i18n/i18n';

function roll(min: number, max: number): number {
  return min + Math.random() * (max - min);
}

/** Schedules the next strike. Exported so a fresh run can seed it lazily. */
function scheduleNext(state: GameState, first: boolean): void {
  const c = BALANCE.calamities;
  const delay = first ? roll(c.firstDelayMin, c.firstDelayMax) : roll(c.intervalMin, c.intervalMax);
  state.run.nextCalamityAt = state.gameTime + delay;
}

function applyCalamity(state: GameState, def: CalamityDef): void {
  const run = state.run;
  const c = BALANCE.calamities;

  if (def.loot) {
    for (const id of RESOURCE_ORDER) {
      const frac = def.loot[id];
      if (!frac) continue;
      const loss = run.resources[id] * frac;
      if (loss > 0) run.resources[id] -= loss;
    }
  }
  if (def.popFraction) {
    const loss = Math.floor(run.population * def.popFraction);
    run.population = Math.max(c.popFloor, run.population - loss);
  }
  if (def.spoilsFraction) {
    run.spoils -= Math.floor(run.spoils * def.spoilsFraction);
  }
  if (def.debuff) {
    // Same pipeline as the Surge tech, just pointing down.
    run.buffs.push({
      id: 'calamity-' + def.id,
      label: tc('calamity.' + def.id + '.name', def.name),
      icon: def.icon,
      prodMult: def.debuff.mult,
      expiresAt: state.gameTime + def.debuff.secs,
    });
  }
  if (def.depleteNodes) {
    const alive = run.nodes.filter((n) => !n.depleted);
    for (let i = 0; i < def.depleteNodes && alive.length > 0; i++) {
      const idx = Math.floor(Math.random() * alive.length);
      const node = alive.splice(idx, 1)[0];
      node.hp = 0;
      node.depleted = true;
      node.respawnAt = state.gameTime + 60;
    }
  }
}

/** Rolls the calamity clock. Called once per sim tick, after combat. */
export function updateCalamities(state: GameState): void {
  const run = state.run;
  const c = BALANCE.calamities;

  // Lazy seed (fresh runs and pre-calamity saves both start at 0).
  if (!run.nextCalamityAt) {
    scheduleNext(state, true);
    return;
  }
  if (state.gameTime < run.nextCalamityAt) return;

  // Onboarding gate: a settlement still learning the game is never struck.
  if (run.wave < c.minWave || run.age < c.minAge) {
    run.nextCalamityAt = state.gameTime + c.retryDelay;
    return;
  }

  const def = CALAMITIES[Math.floor(Math.random() * CALAMITIES.length)];
  applyCalamity(state, def);

  const name = tc('calamity.' + def.id + '.name', def.name);
  bus.emit('calamity', { id: def.id, icon: def.icon, name });
  bus.emit('milestone', {
    title: `${def.icon} ${name}`,
    subtitle: tc('calamity.' + def.id + '.desc', def.desc),
    tone: 'bad',
  });
  bus.emit('shake', { intensity: 7 });
  bus.emit('flash', { color: 0xff5a3c, alpha: 0.22 });

  scheduleNext(state, false);
}

/** UI label helper: seconds until the next possible strike (debug/telemetry). */
export function nextCalamityIn(state: GameState): number {
  return Math.max(0, state.run.nextCalamityAt - state.gameTime);
}
